import * as Yup from "yup";
import { isEmpty, uniq } from "lodash";
import { type FileLike } from "interfaces";
import fileUtils from "./fileUtils";

type FileList = File[] | FileLike[] | unknown[];
export type ValidationValue = FileList | File | FileLike | string | unknown;
type FileValidator<T extends File | FileLike = File | FileLike> = (file: T) => boolean;

const hasExtension = (file: FileLike, extension: string): boolean => file?.name.split(".").pop() === extension;

const isFileListDefined = (value: unknown): value is FileList => !!(value && (value as FileList).length);

const isMp4MovAviFileType = (file: File): boolean =>
  file.type === "video/mp4" || file.type === "video/quicktime" || file.type === "video/avi";

const isMp4FileType = (file: File): boolean => file.type === "video/mp4";

const isMp4OrM4vFileType = (file: File): boolean => file.type === "video/mp4" || file.type === "video/m4v";

const isCsvFileType = (file: File): boolean => file.type === "text/csv";

const validateAllFilesInList = <T extends File | FileLike>(value: unknown, validator: FileValidator<T>): boolean => {
  if (!isFileListDefined(value)) return false;
  return Array.from(value as ArrayLike<T>).every(validator);
};

const validateAnyFileInList = <T extends File | FileLike>(value: unknown, validator: FileValidator<T>): boolean => {
  if (!isFileListDefined(value)) return false;
  return Array.from(value as ArrayLike<T>).some(validator);
};

const xmlUploadFileType = (file: unknown): boolean => {
  if (!file || typeof file !== "object") return false;
  return hasExtension(file as FileLike, "xml");
};

const hasHtmlContent = (value?: string): boolean => {
  if (!value?.trim()) return false;

  try {
    const doc = new DOMParser().parseFromString(value, "text/html");
    const hasText = Boolean(doc.body.textContent?.trim());
    const hasMedia = doc.querySelectorAll("img, video").length > 0;
    return hasText || hasMedia;
  } catch {
    return false;
  }
};

/* istanbul ignore next */
const validateImageFileDimensionsAsync = async (
  imageFile: File,
  maxWidth: number,
  maxHeight: number,
): Promise<boolean> => {
  return new Promise<boolean>((resolve) => {
    fileUtils
      .readFileAsDataUrl(imageFile)
      .then((file) => {
        let img = new Image();
        img.onload = () => {
          const isDimensionsValid = img.width <= maxWidth && img.height <= maxHeight;
          resolve(isDimensionsValid);
        };
        img.src = file as string;
      })
      .catch(() => {
        resolve(false);
      });
  });
};

interface MultiSelectItem {
  length: number;
}

interface MultiSelectValue {
  selected: MultiSelectItem[];
}

function isMultiSelectValue(value: unknown): value is MultiSelectValue {
  return (
    value !== null &&
    typeof value === "object" &&
    "selected" in value &&
    Array.isArray((value as { selected: unknown }).selected)
  );
}

const rules = {
  fileMaxSize(value: ValidationValue): boolean {
    const maxFileSize = 4294967296;
    return validateAllFilesInList<File>(value, (file: File) => file.size <= maxFileSize);
  },
  imageFileMaxSize(value: ValidationValue): boolean {
    const maxFileSize = 5242880;
    return validateAllFilesInList<File>(value, (file: File) => file.size <= maxFileSize);
  },
  teamsImageFileMaxSize(value: ValidationValue): boolean {
    const maxFileSize = 1048576;
    return validateAllFilesInList<File>(value, (file: File) => file.size <= maxFileSize);
  },
  /* istanbul ignore next */
  async teamsImageFileDimensions(value: ValidationValue): Promise<boolean> {
    if (!isFileListDefined(value)) {
      return false;
    }

    const imageFile = value[0] as File;
    const maxWidth = 1024;
    const maxHeight = 1024;
    return await validateImageFileDimensionsAsync(imageFile, maxWidth, maxHeight);
  },
  closedCaptionsFileMaxSize(value: ValidationValue): boolean {
    const maxFileSize = 1048576;
    return validateAllFilesInList(value, (file: File) => file.size <= maxFileSize);
  },
  pdfFileMaxSize(value: ValidationValue): boolean {
    const maxFileSize = 31457280;
    return validateAllFilesInList(value, (file: File) => file.size <= maxFileSize);
  },
  fileMinSize(value: ValidationValue): boolean {
    return validateAllFilesInList<File>(value, (file: File) => file.size !== 0);
  },
  fileNotEmpty(file: unknown): boolean {
    if (!file || typeof file !== "object") return false;
    return (file as File | FileLike)?.size > 0;
  },
  mp4FileType(value: ValidationValue): boolean {
    return validateAllFilesInList<File>(value, isMp4FileType);
  },
  mp4MovAviFileType(value: ValidationValue): boolean {
    return validateAllFilesInList<File>(value, isMp4MovAviFileType);
  },
  mp4m4vFileTypes(value: ValidationValue): boolean {
    return validateAllFilesInList<File>(value, isMp4OrM4vFileType);
  },
  csvFileType(value: ValidationValue): boolean {
    return validateAllFilesInList<File>(value, isCsvFileType);
  },
  anyMp4FileType(value: ValidationValue): boolean {
    return validateAnyFileInList<File>(value, isMp4MovAviFileType);
  },
  imageFileType(value: ValidationValue): boolean {
    const imageType = /image\/*/;
    return validateAllFilesInList<File>(value, (file: File) => imageType.test(file.type)); // NOSONAR
  },
  fileListLength(value: ValidationValue): boolean {
    return isFileListDefined(value) && value.length >= 1;
  },
  fileNameSize(value: ValidationValue): boolean {
    const maxFileNameLength = 120;
    return validateAllFilesInList(value, (file: File | FileLike) => file.name.length <= maxFileNameLength);
  },
  maxLabelLength: 128,
  getMultiSelectItemsValidator(maxLength: number): (value: unknown) => boolean {
    return (value: unknown): boolean => {
      if (isMultiSelectValue(value)) {
        return value.selected.every((item) => item.length <= maxLength);
      }

      return false;
    };
  },

  getMultiSelectValuesValidator(maxLength: number): (value: unknown) => boolean {
    return (value: unknown): boolean => {
      if (Array.isArray(value)) {
        return (value as MultiSelectItem[]).every((item: MultiSelectItem) => item.length <= maxLength);
      }
      return false;
    };
  },

  passwordUniqueCharacters(password: string | null | undefined): boolean {
    const minUniqueCharactersAmount = 3;
    return isEmpty(password) || uniq(password?.split("") || []).length >= minUniqueCharactersAmount;
  },
  usersUploadFileType(value: ValidationValue): boolean {
    return validateAllFilesInList(value, (file: FileLike) => hasExtension(file, "csv"));
  },
  closedCaptionsUploadFileType(value: ValidationValue): boolean {
    return validateAllFilesInList(value, (file: FileLike) => hasExtension(file, "vtt"));
  },
  pdfUploadFileType(value: ValidationValue): boolean {
    return validateAllFilesInList(value, (file: FileLike) => hasExtension(file, "pdf"));
  },
  xmlUploadFileType,
  xmlUploadFileTypeMultiple(value: ValidationValue): boolean {
    return validateAllFilesInList(value, xmlUploadFileType);
  },
  hasNoBrainstormUrls(value: unknown): boolean {
    return (
      isEmpty(value) || !(typeof value === "string" && value.match("(brainstorm|quickhelp|brainstorminc)(\\.com)"))
    );
  },
  htmlWithoutBrainstormDomains(): Yup.StringSchema<string | undefined, object> {
    return Yup.string()
      .test("non-empty-html-body", "Body is required", hasHtmlContent)
      .test("body_restricted_domains", "Body contains restricted domain(s)", this.hasNoBrainstormUrls);
  },
};

export default rules;
